import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Smartphone, Share, PlusSquare, MoreVertical } from "lucide-react";

// Capture Chrome/Edge/Android's install prompt as early as possible (it fires once, before React mounts).
let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn());
  });
}

const isStandalone = () =>
  typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export function InstallAppButton({ variant = "outline", size = "sm", iconOnly = false, className = "" }) {
  const { t } = useTranslation();
  const [, force] = useState(0);
  const [help, setHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const fn = () => { force((n) => n + 1); setInstalled(isStandalone()); };
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  if (installed) return null;

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") toast.success(t("KlenaPro is being added to your device"));
      deferredPrompt = null;
      force((n) => n + 1);
      return;
    }
    setHelp(true);
  };

  const label = t("Add app to my device");
  return (
    <>
      <Button variant={variant} size={iconOnly ? "icon" : size} onClick={install} className={`gap-2 ${className}`} title={label} data-testid="install-app-btn">
        <Smartphone className="h-4 w-4" />{!iconOnly && label}
      </Button>
      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-md" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle>{t("Add KlenaPro to your home screen")}</DialogTitle>
            <DialogDescription>{t("Opens like an app, full screen, straight to klenapro.com — no browser tabs.")}</DialogDescription>
          </DialogHeader>
          {isIOS() ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><Share className="mt-0.5 h-5 w-5 shrink-0" /> <span>{t("1. In Safari, tap the Share button (the square with an arrow) at the bottom of the screen.")}</span></li>
              <li className="flex items-start gap-3"><PlusSquare className="mt-0.5 h-5 w-5 shrink-0" /> <span>{t("2. Scroll down and tap “Add to Home Screen”.")}</span></li>
              <li className="flex items-start gap-3"><Smartphone className="mt-0.5 h-5 w-5 shrink-0" /> <span>{t("3. Tap “Add”. The KlenaPro icon appears on your home screen.")}</span></li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><MoreVertical className="mt-0.5 h-5 w-5 shrink-0" /> <span>{t("1. Open your browser menu (the ⋮ or ⋯ button).")}</span></li>
              <li className="flex items-start gap-3"><PlusSquare className="mt-0.5 h-5 w-5 shrink-0" /> <span>{t("2. Tap “Install app” or “Add to Home screen”.")}</span></li>
              <li className="flex items-start gap-3"><Smartphone className="mt-0.5 h-5 w-5 shrink-0" /> <span>{t("3. Confirm. KlenaPro opens from its own icon from now on.")}</span></li>
            </ol>
          )}
          <p className="text-xs text-muted-foreground">{t("Tip: if your browser has already shown an install banner, this button installs it instantly.")}</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
